import React from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { isEmpty } from 'lodash';
import urljoin from 'url-join';

import ChatSearchField from '~/components/AiChat/components/ChatSearchField';
import { useChatContext } from '~/components/AiChat/hooks/useChat';
import { useChatMixpanel } from '~/components/AiChat/hooks/useChatMixpanel';
import NewChatIcon from '~/components/AiChat/icons/NewChatIcon';
import SearchIcon from '~/components/AiChat/icons/SearchIcon';
import IconButton from '~/components/core/Atomic/Buttons/IconButton';
import CloseIcon from '~/components/icons/CloseIcon';
import ExpandIcon from '~/components/icons/ExpandIcon';
import { CLIVE_CHAT_MIXPANEL_EVENTS } from '~/pocs/mixpanel';

import { EMBEDDED_CHAT_CONFIG } from '../constants';
import { useEmbeddedChatContext } from '../EmbeddedChatContext';

interface EmbeddedChatHeaderActionsProps {
  onClose: () => void;
}

export const EmbeddedChatHeaderActions: React.FC<EmbeddedChatHeaderActionsProps> = ({ onClose }) => {
  const { isSearchActive, searchQuery, setSearchQuery, openSearch, closeSearch } = useEmbeddedChatContext();
  const { createNewChat, exchanges } = useChatContext();
  const { trackChatEvent } = useChatMixpanel();
  const history = useHistory();
  const match = useRouteMatch();

  const hasMessages = !isEmpty(exchanges);

  const handleNewChat = async () => {
    trackChatEvent(CLIVE_CHAT_MIXPANEL_EVENTS.CHAT_NEW_CLICKED, { has_messages: hasMessages });
    await createNewChat();
    closeSearch();
  };

  const handleExpand = () => {
    trackChatEvent(CLIVE_CHAT_MIXPANEL_EVENTS.CHAT_EXPANDED_TO_FULL_SCREEN);
    history.push(urljoin(match.url, 'clive_chat'));
    onClose();
  };

  const handleSearchClick = () => {
    trackChatEvent(CLIVE_CHAT_MIXPANEL_EVENTS.CHAT_SEARCH_OPENED);
    openSearch();
  };

  const handleCloseSearch = () => {
    trackChatEvent(CLIVE_CHAT_MIXPANEL_EVENTS.CHAT_SEARCH_CLOSED);
    closeSearch();
  };

  const handleClose = () => {
    trackChatEvent(CLIVE_CHAT_MIXPANEL_EVENTS.CHAT_CLOSED, { message_count: exchanges.length });
    onClose();
  };

  if (isSearchActive) {
    return (
      <div className="flex w-full origin-right animate-expandSearch items-center gap-4">
        <ChatSearchField value={searchQuery} onChange={setSearchQuery} className="flex-1" />
        <IconButton onClick={handleCloseSearch} className="hover:bg-slate-50 text-blue-600" title="Close Search">
          <CloseIcon iconColor="currentColor" size={EMBEDDED_CHAT_CONFIG.iconSize.actionButton} />
        </IconButton>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-12">
      <IconButton
        onClick={handleNewChat}
        disabled={!hasMessages}
        className={EMBEDDED_CHAT_CONFIG.actionButtonClassName}
        title="New Chat"
      >
        <NewChatIcon size={EMBEDDED_CHAT_CONFIG.iconSize.actionButton} />
      </IconButton>
      <IconButton onClick={handleSearchClick} className={EMBEDDED_CHAT_CONFIG.actionButtonClassName} title="Search">
        <SearchIcon size={EMBEDDED_CHAT_CONFIG.iconSize.actionButton} />
      </IconButton>
      <IconButton onClick={handleExpand} className={EMBEDDED_CHAT_CONFIG.actionButtonClassName} title="Expand">
        <ExpandIcon iconColor="currentColor" size={18} />
      </IconButton>
      <IconButton onClick={handleClose} className={EMBEDDED_CHAT_CONFIG.actionButtonClassName} title="Close">
        <CloseIcon iconColor="currentColor" size={EMBEDDED_CHAT_CONFIG.iconSize.actionButton} />
      </IconButton>
    </div>
  );
};
